import React from 'react';
import { Flex, Typography } from 'antd';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { splitBalance } from 'shared/libs/utils/helpers';
import { getAvailableBalanceFromLedger } from 'shared/libs/utils/balance';
import classes from './DualBalance.module.scss';

type DualBalanceSize = 'xl' | 'lg' | 'md' | 'sm';

type DualBalanceProps = {
  ledgerBalance: number;
  secured?: boolean;
  size?: DualBalanceSize;
  variant?: 'default' | 'card';
  showLedger?: boolean;
  showLabels?: boolean;
  className?: string;
  idPrefix?: string;
};

type BalanceAmountProps = {
  amount: number;
  secured?: boolean;
  size: DualBalanceSize;
  variant: 'default' | 'card';
  secondary?: boolean;
  id?: string;
};

const BalanceAmount: React.FC<BalanceAmountProps> = ({
  amount,
  secured,
  size,
  variant,
  secondary = false,
  id,
}) => {
  const [wholePart, fractionalPart] = splitBalance(amount);

  return (
    <Typography.Text
      id={id}
      className={clsx(
        classes.amount,
        classes[`amount_${size}`],
        classes[`amount_${variant}`],
        secondary && classes.amount_secondary,
      )}
    >
      {secured ? '*' : wholePart},
      <Typography.Text className={classes.fractionalPart}>
        {secured ? '**' : fractionalPart} €
      </Typography.Text>
    </Typography.Text>
  );
};

const DualBalance: React.FC<DualBalanceProps> = ({
  ledgerBalance,
  secured = false,
  size = 'lg',
  variant = 'default',
  showLedger = true,
  showLabels = true,
  className,
  idPrefix,
}) => {
  const { formatMessage: t } = useIntl();
  const availableBalance = getAvailableBalanceFromLedger(ledgerBalance);

  return (
    <Flex
      justify="flex-start"
      align="flex-end"
      gap={20}
      className={clsx(classes.wrapper, className)}
    >
      <Flex vertical gap={2} className={classes.mainColumn}>
        {showLabels && (
          <Typography.Text
            className={clsx(classes.label, classes[`label_${size}`], classes[`label_${variant}`])}
          >
            {t({ id: 'balance.available', defaultMessage: 'Available balance' })}
          </Typography.Text>
        )}
        <BalanceAmount
          amount={availableBalance}
          secured={secured}
          size={size}
          variant={variant}
          id={idPrefix ? `${idPrefix}-available` : undefined}
        />
      </Flex>
      {showLedger && (
        <Flex vertical gap={2} align="flex-end" className={classes.ledgerColumn}>
          {showLabels && (
            <Typography.Text
              className={clsx(
                classes.label,
                classes.label_secondary,
                classes[`label_${size}`],
              )}
            >
              {t({ id: 'balance.ledger', defaultMessage: 'Ledger balance' })}
            </Typography.Text>
          )}
          <BalanceAmount
            amount={ledgerBalance}
            secured={secured}
            size={size}
            variant={variant}
            secondary
            id={idPrefix ? `${idPrefix}-ledger` : undefined}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default DualBalance;
